import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';
import { useAppStore } from '@/store/useAppStore';
import type { Destination } from '@/shared/types';

export interface GetAllCountries {
  destinationId: number;
  destinationName: string;
  destinationCode: string;
  areaType: string;
  areaCoverage: string[];
}

interface UseGetAllCountriesReturn {
  destinations: GetAllCountries[];
  inboundlocationOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useGetAllCountries = (): UseGetAllCountriesReturn => {
  const [destinations, setGetAllCountries] = useState<GetAllCountries[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setAllCountries } = useAppStore();

  const fetchGetAllCountries = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        destinations: GetAllCountries[];
      }>(
        '/api/destinations/by-area?area=country',
        {},
        60 * 60 * 1000 // 60 minutes TTL - countries very rarely change
      );

      if (response.success && response.destinations) {
        const sortedAllCountries = [...response.destinations].sort((a, b) =>
          a.destinationName.localeCompare(b.destinationName)
        );

        console.log("Fetched all countries (cached/sorted):", sortedAllCountries);

        setAllCountries(
          sortedAllCountries.map(
            (country): Destination => ({
              destinationId: country.destinationId,
              destinationName: country.destinationName,
              destinationCode: country.destinationCode,
              areaType: country.areaType,
              areaCoverage: country.areaCoverage,
            })
          )
        );

        setGetAllCountries(sortedAllCountries);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching all countries:', err);
      setError('Failed to fetch all countries');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since api and setters are stable

  useEffect(() => {
    fetchGetAllCountries();
  }, [fetchGetAllCountries]); // ✅ Now includes the dependency

  // Convert countries to location options format for dropdowns
  const inboundlocationOptions = destinations.map(destination => destination.destinationName);

  return {
    destinations,
    inboundlocationOptions,
    loading,
    error,
    refetch: fetchGetAllCountries
  };
};